/**
 * Budget-limit presentation shared by both budget cards — the full
 * `LiteLLMBudgetWidget` and the expanded "all limits" view of
 * `LiteLLMBudgetGauges` — so a limit reads identically wherever it is shown.
 */
import type { ReactNode } from 'react';
import { Link, Text } from '@backstage/ui';
import { BudgetLimit, fmtBudgetDuration, budgetTone } from '../budget';
import { fmtUsd } from '../format';
import { Meter } from './ui';
import styles from './BudgetLimitList.module.css';

/** Spend-vs-cap meter card for a single limit. */
export const LimitCard = ({ limit }: { limit: BudgetLimit }) => {
  const tone = budgetTone(limit.pct);
  const pct = Math.round(limit.pct);
  const pastSoftLimit =
    limit.softLimit !== undefined && limit.spend >= limit.softLimit;
  const reset = fmtBudgetDuration(limit.budgetDuration);
  return (
    <div className={styles.limit}>
      <div className={styles.limitHead}>
        <div className={styles.limitTitle}>
          <Text
            variant="body-small"
            className={styles.limitLabel}
            title={
              limit.sublabel
                ? `${limit.label} · ${limit.sublabel}`
                : limit.label
            }
          >
            {limit.label}
          </Text>
          {limit.sublabel && (
            <Text variant="body-x-small" className={styles.limitSub}>
              {limit.sublabel}
            </Text>
          )}
        </div>
        {/* Redacted team budgets carry only a percentage, never dollars. */}
        <Text variant="body-small" className={styles.limitFigure}>
          {limit.hidden ? (
            `${pct}% used`
          ) : (
            <>
              {fmtUsd(limit.spend)}
              <span className={styles.limitCap}> / {fmtUsd(limit.budget)}</span>
            </>
          )}
        </Text>
      </div>
      <div className={styles.limitMeter}>
        <Meter
          value={limit.pct}
          tone={tone}
          height={6}
          ariaLabel={`${limit.label} budget used: ${pct}%`}
        />
      </div>
      <div className={styles.limitFoot}>
        <Text variant="body-x-small" className={styles.limitMeta}>
          {limit.hidden
            ? 'Hidden by admin'
            : `${pct}% of budget${
                pastSoftLimit ? ' · soft-limit warning' : ''
              }`}
        </Text>
        <Text variant="body-x-small" className={styles.limitMeta}>
          {reset ? `resets ${reset}` : 'never resets'}
        </Text>
      </div>
    </div>
  );
};

/** A LimitCard prefixed with a small KEY / USER / TEAM tag. */
export const TaggedLimit = ({ limit }: { limit: BudgetLimit }) => {
  const tone = budgetTone(limit.pct);
  return (
    <div>
      <Text
        variant="body-x-small"
        className={styles.kindTag}
        data-tone={tone === 'accent' ? 'neutral' : tone}
      >
        {limit.kind}
      </Text>
      <LimitCard limit={limit} />
    </div>
  );
};

/** Budgeted keys past the visible cap — plain text when no href is given. */
export const MoreKeysNote = ({
  count,
  href,
  label,
}: {
  count: number;
  href?: string;
  label?: string;
}) => {
  const text =
    label ??
    `+${count} more budgeted key${count > 1 ? 's' : ''} further from the cap`;
  if (!href) {
    return (
      <Text variant="body-x-small" className={styles.limitMeta}>
        {text}
      </Text>
    );
  }
  return (
    <div className={styles.moreKeys}>
      <Link href={href} variant="body-x-small" className={styles.moreKeysLink}>
        {text} →
      </Link>
    </div>
  );
};

/** Flat list of concrete limits across every level, as tagged meter cards. */
export const BudgetLimitList = ({
  limits,
  hiddenKeyCount = 0,
  keysHref,
}: {
  limits: BudgetLimit[];
  /** Keys beyond the supplied limits — rendered as a trailing note. */
  hiddenKeyCount?: number;
  keysHref?: string;
}) => (
  <div className={styles.list}>
    {limits.map(limit => (
      <TaggedLimit
        key={`${limit.kind}:${limit.sublabel ?? limit.label}`}
        limit={limit}
      />
    ))}
    {hiddenKeyCount > 0 && (
      <MoreKeysNote count={hiddenKeyCount} href={keysHref} />
    )}
  </div>
);

/** Inset panel holding a nested list of limits inside a card. */
export const LimitListPanel = ({ children }: { children: ReactNode }) => (
  <div className={styles.panel}>{children}</div>
);
